import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  api,
  type AuditLog,
  type DashboardStats,
  type Course,
  type CourseEdition,
  type AuthUser,
} from "@/lib/api";
import StatCard from "@/components/base/StatCard";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";

function monthOptions(count = 18) {
  const opts: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth() - i, 1));
    const value = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("es-MX", { month: "long", year: "numeric", timeZone: "UTC" });
    opts.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return opts;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeMetricTab, setActiveMetricTab] = useState<"cursos" | "pagos">("cursos");
  const [expandedActivity, setExpandedActivity] = useState(false);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [editions, setEditions] = useState<CourseEdition[]>([]);
  const [instructors, setInstructors] = useState<AuthUser[]>([]);
  const [courseFilter, setCourseFilter] = useState("");
  const [editionFilter, setEditionFilter] = useState("");
  const [instructorFilter, setInstructorFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);

  const months = useMemo(() => monthOptions(24), []);

  const categories = useMemo(() => {
    const set = new Set(courses.map((c) => c.category).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
  }, [courses]);

  useEffect(() => {
    Promise.all([
      api.getCourses().catch(() => [] as Course[]),
      api.getEditions().catch(() => [] as CourseEdition[]),
      api.getInstructors().catch(() => [] as AuthUser[]),
    ]).then(([c, e, i]) => {
      setCourses(c);
      setEditions(e);
      setInstructors(
        i.filter((u) => u.role === "instructor" || u.role === "admin" || u.role === "super_admin")
      );
    });
  }, []);

  const filteredCourses = useMemo(() => {
    if (!categoryFilter) return courses;
    return courses.filter((c) => c.category === categoryFilter);
  }, [courses, categoryFilter]);

  const filteredEditions = useMemo(() => {
    return editions.filter((ed) => {
      if (courseFilter && ed.course_id !== courseFilter) return false;
      if (instructorFilter && ed.instructor_id !== instructorFilter) return false;
      if (categoryFilter) {
        const cat = ed.course?.category ?? courses.find((c) => c.id === ed.course_id)?.category;
        if (cat !== categoryFilter) return false;
      }
      return true;
    });
  }, [editions, courseFilter, instructorFilter, categoryFilter, courses]);

  useEffect(() => {
    if (editionFilter && !filteredEditions.some((e) => e.id === editionFilter)) {
      setEditionFilter("");
    }
  }, [filteredEditions, editionFilter]);

  useEffect(() => {
    if (courseFilter && categoryFilter) {
      const course = courses.find((c) => c.id === courseFilter);
      if (course && course.category !== categoryFilter) {
        setCourseFilter("");
        setEditionFilter("");
      }
    }
  }, [categoryFilter, courseFilter, courses]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [statsData, logsData] = await Promise.all([
          api.getDashboardStats({
            courseId: courseFilter || undefined,
            editionId: editionFilter || undefined,
            instructorId: instructorFilter || undefined,
            category: categoryFilter || undefined,
            month: monthFilter || undefined,
            dateFrom: monthFilter ? undefined : dateFrom || undefined,
            dateTo: monthFilter ? undefined : dateTo || undefined,
          }),
          api.getAuditLogs(),
        ]);
        if (cancelled) return;
        setStats(statsData);
        setAuditLogs(logsData);
      } catch {
        if (cancelled) return;
        setStats(null);
        setAuditLogs([]);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [courseFilter, editionFilter, instructorFilter, categoryFilter, monthFilter, dateFrom, dateTo]);

  const filterSummary = useMemo(() => {
    const parts: string[] = [];
    if (categoryFilter) parts.push(categoryFilter);
    const courseName = courses.find((c) => c.id === courseFilter)?.name;
    if (courseName) parts.push(courseName);
    const edition = editions.find((e) => e.id === editionFilter);
    if (edition) parts.push(edition.edition_label || edition.location || "Edición");
    const instructor = instructors.find((i) => i.id === instructorFilter)?.full_name;
    if (instructor) parts.push(instructor);
    if (monthFilter) {
      const m = months.find((x) => x.value === monthFilter)?.label;
      if (m) parts.push(m);
    } else if (dateFrom || dateTo) {
      parts.push(`${dateFrom || "…"} → ${dateTo || "…"}`);
    }
    return parts;
  }, [courses, editions, instructors, courseFilter, editionFilter, instructorFilter, categoryFilter, monthFilter, dateFrom, dateTo, months]);

  const recentActivity = useMemo(() => {
    return [...auditLogs].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [auditLogs]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

  const clearFilters = () => {
    setCourseFilter("");
    setEditionFilter("");
    setInstructorFilter("");
    setCategoryFilter("");
    setMonthFilter("");
    setDateFrom("");
    setDateTo("");
  };

  const hasActiveFilters = Boolean(
    courseFilter || editionFilter || instructorFilter || categoryFilter || monthFilter || dateFrom || dateTo
  );

  const pendingTasks = useMemo(() => {
    if (!stats) return [];
    const tasks: { id: string; icon: string; label: string; display: string; color: string; link: string }[] = [];
    if (stats.pendingEnrollments > 0) {
      tasks.push({
        id: "t0", icon: "ri-user-add-line", label: "Inscripciones por Aprobar",
        display: String(stats.pendingEnrollments), color: "blue", link: "/pagos",
      });
    }
    if (stats.pendingEvaluations > 0) {
      tasks.push({
        id: "t1", icon: "ri-check-double-line", label: "Evaluaciones Pendientes",
        display: String(stats.pendingEvaluations), color: "yellow", link: "/portal-evaluador",
      });
    }
    if ((stats.pendingBalancesCount ?? 0) > 0) {
      tasks.push({
        id: "t2", icon: "ri-money-dollar-circle-line", label: "Pagos por Cobrar",
        display: formatCurrency(stats.pending), color: "red", link: "/pagos",
      });
    }
    return tasks;
  }, [stats]);

  const paymentStatusData = useMemo(() => {
    if (!stats) return [];
    return [
      { name: "Cobrado", value: stats.collected, color: "#2e7d32" },
      { name: "Pendiente", value: stats.pending, color: "#c62828" },
    ];
  }, [stats]);

  const revenueChartData = useMemo(() => {
    if (!stats) return [];
    return [
      { mes: "Cobrado", ingresos: stats.collected },
      { mes: "Pendiente", ingresos: stats.pending },
    ];
  }, [stats]);

  const getActionIcon = useCallback((action: string) => {
    if (action.includes("asistencia")) return "ri-user-follow-line";
    if (action.includes("Aprobación") || action.includes("Aprobó")) return "ri-check-double-line";
    if (action.includes("certificado")) return "ri-award-line";
    if (action.includes("Creación") || action.includes("Creó")) return "ri-add-circle-line";
    if (action.includes("pago") || action.includes("recibo")) return "ri-money-dollar-circle-line";
    if (action.includes("Rechazo") || action.includes("Rechazó")) return "ri-close-circle-line";
    if (action.includes("revisión") || action.includes("Solicitó")) return "ri-loop-left-line";
    if (action.includes("observación")) return "ri-sticky-note-line";
    return "ri-time-line";
  }, []);

  const getActionColor = useCallback((action: string) => {
    if (action.includes("Aprobación") || action.includes("Aprobó") || action.includes("certificado")) {
      return "text-green-400 bg-green-500/15 border-green-500/20";
    }
    if (action.includes("Rechazo") || action.includes("Rechazó")) {
      return "text-red-400 bg-red-500/15 border-red-500/20";
    }
    if (action.includes("revisión") || action.includes("Solicitó")) {
      return "text-yellow-400 bg-yellow-500/15 border-yellow-500/20";
    }
    if (action.includes("asistencia")) return "text-blue-400 bg-blue-500/15 border-blue-500/20";
    return "text-[var(--color-text-muted)] bg-[var(--color-bg-tertiary)] border-[var(--color-border)]";
  }, []);

  if (loading && !stats) {
    return (
      <div className="text-center py-20">
        <i className="ri-loader-4-line text-3xl text-[var(--color-text-muted)] animate-spin"></i>
        <p className="text-sm text-[var(--color-text-muted)] mt-3">Cargando dashboard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            Bienvenido, {user?.full_name.split(" ")[0]}
          </h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            Panel de Control Ejecutivo &mdash; KSM Training Management System
            {filterSummary.length > 0 ? (
              <span className="text-[var(--color-accent-oil)]"> · {filterSummary.join(" · ")}</span>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => navigate("/cursos")} className="btn-secondary flex items-center gap-2 text-xs cursor-pointer">
            <i className="ri-add-line"></i>
            Nuevo Curso
          </button>
          <button onClick={() => navigate("/estudiantes")} className="btn-primary flex items-center gap-2 text-xs cursor-pointer">
            <i className="ri-user-add-line"></i>
            Ver Estudiantes
          </button>
        </div>
      </div>

      <div className="card space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] flex items-center gap-2">
            <i className="ri-filter-3-line text-[var(--color-accent-oil)]"></i>
            Filtros del dashboard
          </h3>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-xs text-[var(--color-accent)] hover:underline cursor-pointer">
              Limpiar filtros
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">Tipo de curso</label>
            <select
              className="input-field"
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setEditionFilter("");
              }}
              aria-label="Filtrar por tipo de curso"
            >
              <option value="">Todos los tipos</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">Curso</label>
            <select
              className="input-field"
              value={courseFilter}
              onChange={(e) => {
                setCourseFilter(e.target.value);
                setEditionFilter("");
              }}
              aria-label="Filtrar por curso"
            >
              <option value="">Todos los cursos</option>
              {filteredCourses.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">Edición</label>
            <select
              className="input-field"
              value={editionFilter}
              onChange={(e) => setEditionFilter(e.target.value)}
              aria-label="Filtrar por edición"
            >
              <option value="">Todas las ediciones</option>
              {filteredEditions.map((ed) => (
                <option key={ed.id} value={ed.id}>
                  {(ed.edition_label || ed.location || "Edición") +
                    (ed.course?.name ? ` — ${ed.course.name}` : "")}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">Instructor</label>
            <select
              className="input-field"
              value={instructorFilter}
              onChange={(e) => {
                setInstructorFilter(e.target.value);
                setEditionFilter("");
              }}
              aria-label="Filtrar por instructor"
            >
              <option value="">Todos los instructores</option>
              {instructors.map((i) => (
                <option key={i.id} value={i.id}>{i.full_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">Mes</label>
            <select
              className="input-field"
              value={monthFilter}
              onChange={(e) => {
                setMonthFilter(e.target.value);
                if (e.target.value) {
                  setDateFrom("");
                  setDateTo("");
                }
              }}
              aria-label="Filtrar por mes"
            >
              <option value="">Todos los meses</option>
              {months.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">Desde</label>
            <input
              type="date"
              className="input-field"
              value={dateFrom}
              disabled={Boolean(monthFilter)}
              onChange={(e) => {
                setDateFrom(e.target.value);
                if (e.target.value) setMonthFilter("");
              }}
              aria-label="Fecha desde"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">Hasta</label>
            <input
              type="date"
              className="input-field"
              value={dateTo}
              disabled={Boolean(monthFilter)}
              onChange={(e) => {
                setDateTo(e.target.value);
                if (e.target.value) setMonthFilter("");
              }}
              aria-label="Fecha hasta"
            />
          </div>
        </div>
        <p className="text-[11px] text-[var(--color-text-muted)]">
          Mes y rango de fechas filtran por la fecha de inicio de la edición. El resto de métricas (estudiantes, pagos, certificados) se recalculan con esos filtros.
        </p>
      </div>

      {loading && stats && (
        <p className="text-xs text-[var(--color-text-muted)] flex items-center gap-2">
          <i className="ri-loader-4-line animate-spin"></i> Actualizando métricas...
        </p>
      )}

      {stats && !stats.smtpConfigured && (
        <div className="card border-l-4 border-l-yellow-500 bg-yellow-500/5">
          <p className="text-sm text-yellow-400 font-medium"><i className="ri-mail-warning-line"></i> SMTP no configurado</p>
          <p className="text-xs text-[var(--color-text-muted)] mt-1">Los correos no se enviarán hasta configurar SMTP_USER y SMTP_PASS en el servidor.</p>
        </div>
      )}

      {pendingTasks.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {pendingTasks.map((task) => (
            <button
              key={task.id}
              onClick={() => navigate(task.link)}
              className={`card flex items-center gap-4 cursor-pointer hover:border-[var(--color-border-light)] transition-all group text-left ${
                task.color === "yellow" ? "border-yellow-500/20 bg-yellow-500/3" :
                task.color === "red" ? "border-red-500/20 bg-red-500/3" :
                task.color === "blue" ? "border-blue-500/20 bg-blue-500/3" :
                "border-green-500/20 bg-green-500/3"
              }`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                task.color === "yellow" ? "bg-yellow-500/15 text-yellow-400" :
                task.color === "red" ? "bg-red-500/15 text-red-400" :
                task.color === "blue" ? "bg-blue-500/15 text-blue-400" :
                "bg-green-500/15 text-green-400"
              }`}>
                <i className={`${task.icon} text-lg`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[var(--color-text-primary)]">{task.display}</p>
                <p className="text-xs text-[var(--color-text-muted)]">{task.label}</p>
              </div>
              <i className="ri-arrow-right-s-line text-[var(--color-text-muted)] group-hover:translate-x-1 transition-transform"></i>
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon="ri-book-open-line"
          label="Ediciones activas"
          value={stats?.activeEditions ?? stats?.activeCourses ?? 0}
        />
        <StatCard
          icon="ri-stack-line"
          label="Cursos catálogo"
          value={stats?.catalogCourses ?? 0}
        />
        <StatCard
          icon="ri-user-star-line"
          label="Estudiantes"
          value={stats?.totalStudents ?? 0}
        />
        <StatCard
          icon="ri-award-line"
          label="Certificados"
          value={stats?.totalCertificates ?? 0}
          accentClass="bg-green-500/10 text-green-400"
        />
        <StatCard
          icon="ri-check-double-line"
          label="Eval. pendientes"
          value={stats?.pendingEvaluations ?? 0}
          accentClass={(stats?.pendingEvaluations ?? 0) > 0 ? "bg-yellow-500/10 text-yellow-400" : "bg-green-500/10 text-green-400"}
        />
      </div>

      {(stats?.pendingEnrollments ?? 0) > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard icon="ri-user-add-line" label="Inscrip. por aprobar" value={stats?.pendingEnrollments ?? 0}
            accentClass="bg-blue-500/10 text-blue-400" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
              {activeMetricTab === "cursos" ? "Resumen General" : "Ingresos"}
            </h3>
            <div className="flex items-center gap-1 bg-[var(--color-bg-tertiary)] rounded-lg p-0.5">
              <button
                onClick={() => setActiveMetricTab("cursos")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeMetricTab === "cursos" ? "bg-[var(--color-accent)] text-white" : "text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                }`}
              >
                General
              </button>
              <button
                onClick={() => setActiveMetricTab("pagos")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeMetricTab === "pagos" ? "bg-[var(--color-accent)] text-white" : "text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                }`}
              >
                Ingresos
              </button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            {activeMetricTab === "cursos" ? (
              <BarChart
                data={[
                  { name: "Cursos", valor: stats?.activeCourses ?? 0 },
                  { name: "Estudiantes", valor: stats?.totalStudents ?? 0 },
                  { name: "Certificados", valor: stats?.totalCertificates ?? 0 },
                  { name: "Eval. Pend.", valor: stats?.pendingEvaluations ?? 0 },
                ]}
                barGap={4}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="name" tick={{ fill: "var(--color-text-muted)", fontSize: 11 }} axisLine={{ stroke: "var(--color-border)" }} />
                <YAxis tick={{ fill: "var(--color-text-muted)", fontSize: 11 }} axisLine={{ stroke: "var(--color-border)" }} allowDecimals={false} />
                <Tooltip contentStyle={{ backgroundColor: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: "8px", fontSize: "12px", color: "var(--color-text-primary)" }} />
                <Bar dataKey="valor" fill="var(--color-accent-oil)" radius={[6, 6, 0, 0]} barSize={40} name="Total" />
              </BarChart>
            ) : (
              <AreaChart data={revenueChartData}>
                <defs>
                  <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-accent-oil)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--color-accent-oil)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="mes" tick={{ fill: "var(--color-text-muted)", fontSize: 11 }} axisLine={{ stroke: "var(--color-border)" }} />
                <YAxis tick={{ fill: "var(--color-text-muted)", fontSize: 11 }} axisLine={{ stroke: "var(--color-border)" }} />
                <Tooltip contentStyle={{ backgroundColor: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: "8px", fontSize: "12px", color: "var(--color-text-primary)" }} formatter={(value: number) => formatCurrency(value)} />
                <Area type="monotone" dataKey="ingresos" stroke="var(--color-accent-oil)" fill="url(#revenueGrad)" strokeWidth={2} name="Ingresos" />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">Estado de Pagos</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={paymentStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                {paymentStatusData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: "8px", fontSize: "12px", color: "var(--color-text-primary)" }} formatter={(value: number) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-col gap-2 mt-2">
            {paymentStatusData.map((item) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <span className="text-xs text-[var(--color-text-secondary)]">{item.name}</span>
                </div>
                <span className="text-xs font-medium text-[var(--color-text-primary)]">{formatCurrency(item.value)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center">
                <p className="text-xs text-[var(--color-text-muted)]">Cobrado</p>
                <p className="text-lg font-bold text-green-400">{formatCurrency(stats?.collected ?? 0)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-[var(--color-text-muted)]">Pendiente</p>
                <p className="text-lg font-bold text-red-400">{formatCurrency(stats?.pending ?? 0)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] flex items-center gap-2">
            <i className="ri-history-line text-[var(--color-accent-oil)]"></i>
            Actividad Reciente
          </h3>
          <button onClick={() => navigate("/auditoria")} className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors cursor-pointer">
            Ver auditoría completa
          </button>
          {recentActivity.length > 6 && (
            <button onClick={() => setExpandedActivity((v) => !v)} className="text-xs text-[var(--color-accent)] hover:underline cursor-pointer ml-3">
              {expandedActivity ? "Ver menos" : "Ver más"}
            </button>
          )}
        </div>
        {recentActivity.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)] text-center py-8">Sin actividad reciente</p>
        ) : (
          <div className="space-y-0 max-h-[320px] overflow-y-auto pr-1">
            {(expandedActivity ? recentActivity : recentActivity.slice(0, 6)).map((log, idx, arr) => {
              const icon = getActionIcon(log.action);
              const colorClass = getActionColor(log.action);
              return (
                <div
                  key={log.id}
                  className={`flex items-start gap-3 py-3 ${
                    idx < arr.length - 1 ? "border-b border-[var(--color-border)]" : ""
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 border ${colorClass}`}>
                    <i className={`${icon} text-xs`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[var(--color-text-primary)] truncate">{log.details}</p>
                    <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                      {log.user_name} &middot;{" "}
                      {new Date(log.created_at).toLocaleString("es-VE", {
                        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
